import { useGenericCRUD } from '@/hooks/generic/useGenericCRUD';

export interface Contact {
  id: string;
  user_id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: {
    name: string;
    company_type: string;
    status: string;
  };
  company_id?: string;
  job_title?: string;
  department?: string;
  is_primary_contact?: boolean;
  is_decision_maker?: boolean;
  linkedin_url?: string;
  contact_type: 'lead' | 'client' | 'vendor' | 'contractor';
  status: 'new' | 'contacted' | 'qualified' | 'active' | 'inactive';
  notes?: string;
  tags?: string[];
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  business_card_image_path?: string;
  business_card_image_url?: string;
  extraction_metadata?: {
    confidence?: number;
    rawExtraction?: any;
  };
  created_at: string;
  updated_at: string;
}

export interface ContactFormData {
  name: string;
  email?: string;
  phone?: string;
  company_id?: string;
  job_title?: string;
  department?: string;
  is_primary_contact?: boolean;
  is_decision_maker?: boolean;
  linkedin_url?: string;
  contact_type: 'lead' | 'client' | 'vendor' | 'contractor';
  status: 'new' | 'contacted' | 'qualified' | 'active' | 'inactive';
  notes?: string;
  tags?: string[];
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  business_card_image_path?: string;
  business_card_image_url?: string;
  extraction_metadata?: {
    confidence?: number;
    rawExtraction?: any;
  };
}

export function useContacts(options?: { enabled?: boolean }) {
  const genericCRUD = useGenericCRUD<Contact, ContactFormData>({
    tableName: 'contacts',
    queryKey: 'contacts',
    select: '*, company:companies(name, company_type, status)',
    orderBy: { column: 'created_at', ascending: false },
    toastMessages: {
      create: 'Contact created successfully',
      update: 'Contact updated successfully',
      delete: 'Contact deleted successfully',
    },
    enabled: options?.enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    contacts: genericCRUD.data,
    loading: genericCRUD.isLoading,
    createContact: genericCRUD.createMutation.mutateAsync,
    updateContact: (id: string, data: Partial<ContactFormData>) => 
      genericCRUD.updateMutation.mutateAsync({ id, data }),
    deleteContact: genericCRUD.deleteMutation.mutateAsync,
    refetch: genericCRUD.refetch,
  };
}